import React from 'react';
import styled, { css } from 'styled-components';
import { any, string, bool } from 'prop-types';
import { withTranslation } from '../../../hoc';
// import LoadingSpinner from '../../../../assets/images/loading.svg'
import { Loading as LoadingSpinner, FlexContainer, Icon, If } from '../../common';
import { GRAY_DARK, GREEN_DARK, WHITE } from '../../../utils/colors';

const buttonWidth = ({ width, fitContent }) => {
    if (fitContent) return css`width: fit-content;`;
    if (width) {
        return css`
            min-width: ${width}px;
            max-width: ${width}px;
        `;
    } else {
        return css`
            width: 100%;
        `;
    }
};

const disable = (disabled) => {
    return disabled && css`
        background-color: #ffffff;
        color: #d3d3d3;
        border: 1px solid #d3d3d3;
        cursor: not-allowed;

        :hover {
            background-color: #ffffff;
            color: #d3d3d3;
            border: 1px solid #d3d3d3;
            cursor: not-allowed;
        }
    `;
};

const ButtonContainer = styled.button`
    ${({ width, fitContent }) => buttonWidth({ width, fitContent })}
    min-height: ${({ height }) => height || '34px'};
    max-height: 34px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    align-items: center;
    text-align: center;
    display: ${({ display = 'flex' }) => display || 'flex'};
    line-height: normal;
    text-transform: ${({ textTransform = 'capitalize' }) => css`${textTransform}`};
    font-stretch: normal;
    letter-spacing: normal;
    justify-content: center;
    font-size: 12px;
    border: 1px solid white;
    padding: 6px 16px 6px 16px !important;
    margin: ${({ margin = '0', top, right, bottom, left }) => `${top || margin}px ${right || margin}px ${bottom || margin}px ${left || margin}px`};

    :focus {
        outline: 0;
    }

    ${({ minWidth }) => minWidth && `min-width: ${minWidth};`}
    ${({ maxWidth }) => maxWidth && `max-width: ${maxWidth};`}


    ${({ bold = false }) => `font-weight: ${bold ? 'bold' : 'normal'};`}
    ${({ semibold = true }) => `font-weight: ${semibold ? '600' : 'normal'};`}

    // PRIMARY BUTTON
    ${({ secondary, disabled, link, loading }) => {
        if ((!secondary && !link && !loading) && disabled) {
            return css`
                background-color: #c0c0c0;
                border: 1px solid #c0c0c0;
                color: #ffffff;

                :hover {
                    background-color: #c0c0c0;
                    border-color: #c0c0c0;
                    color: #ffffff;
                    cursor: not-allowed;
                }
            `;
        }
        if (!secondary && !link && !loading) {
            return css`
                background-color: #a4c735;
                border: 1px solid #a4c735;
                color: #ffffff;

                :hover {
                    background-color: #89a503;                    ;
                    border-color: #89a503;
                    color: #ffffff;
                }
            `;
        }
    }}

    // SECONDARY BUTTON
    ${({ secondary }) => secondary && css`
        background-color: #ffffff;
        color: #a4c735;
        border: 1px solid #a4c735;

        :hover {
            background-color: #a4c735;
            color: #ffffff;
        }
        ${({ disabled }) => disable(disabled)}
    `}

    // SECONDARY BUTTON QUESTION
    ${({ secondaryQuestion }) => secondaryQuestion && css`
        background-color: #f4f4f4;
        color: #a4c735;
        border: 1px solid #a4c735;

        :hover {
            background-color: #a4c735;
            color: #ffffff;
        }
        ${({ disabled }) => disable(disabled)}
    `}

    ${({ link }) => link && css`
        background-color: #ffffff;
        color: ${({ color = '#1fa02e' }) => color};
        text-transform: ${({ textTransform = 'capitalize' }) => css`${textTransform}`};
        font-size: 12px;
        border: 0px;

        :hover {
            background-color: #ffffff;
            text-decoration: underline ${({ color = '#1fa02e' }) => color};
            border: 0px;
            color: ${({ color = '#1fa02e' }) => color};
        }
        ${({ disabled }) => disabled && css`
            ${disable(disabled)}
            border: 0;
            :hover {
                border: 0;
                text-decoration: none;
            }
        `}
    `}

    ${({ flat }) => flat && css`
        background-color: #FFFFFF;
        color: #1FA02E;
        border: 0px;
        justify-content: flex-start;
        :hover {
            background-color: #FFFFFF;
            text-decoration: underline;
            color: #1FA02E;
        }
        ${({ disabled }) => disabled && css`
            color: #888888;
            text-decoration: none;
            :hover {
                color: #888888;
                text-decoration: none;
            }
        `}
    `}

    ${({ unavailable }) => unavailable && disable(true)}

    ${({ loading, secondary }) => loading && css`
        background-color: ${secondary ? '#ffffff' : '#a4c735'};
        border: 1px solid #a4c735;
        color: ${secondary ? '#a4c735' : '#ffffff'};
        cursor: wait;
        ${({ width, fitContent }) => buttonWidth({ width, fitContent })}

        :hover {
            background-color: #a4c735;
            border: 1px solid #a4c735;
            cursor: wait;
        }
    `}

    ${({ backgroundColor }) => backgroundColor && css`
        background-color: ${backgroundColor} !important;
    `}

    ${({ padding }) => padding && css`
    padding: ${padding} !important;
    `}
`;

const Spinner = styled.img`
    width: 15px;
    height: 15px;
    margin-right: 5px;
    margin-top: -3px;
`;

const Button = ({ text, children, htmlType, icon, ...props }) => {
    const { secondary, link, flat, loading, disabled } = props;
    const spinnerColor = (secondary || link) ? '#a4c735' : '#ffffff';
    const iconColor = () => {
        // First array value is for disabled state. Otherwise, the second.
        if (flat) return [GRAY_DARK, GREEN_DARK];
        return [WHITE];
    };
    return (
        <ButtonContainer type={htmlType} {...props}>
            <FlexContainer>
                <If
                    condition={icon}
                    then={<Icon name={icon} size={16} right={12} color={disabled ? iconColor()[0] : iconColor()[1] || iconColor()[0]} />}
                />
                {loading && <LoadingSpinner loading={loading} style={{ fontSize: 18, color: spinnerColor }} />}
                { !loading && (children || text) }
            </FlexContainer>
        </ButtonContainer>
    );
};

Button.propTypes = {
    htmlType: string,
    maxWidth: string,
    children: any,
    text: string,
    loading: bool,
    secondary: bool,
    link: bool,
    flat: bool,
    icon: string,
    disabled: bool
};

Button.defaultProps = {
    htmlType: 'button'
};

export default withTranslation(Button, ['text']);
